import React, { createContext, useContext, useEffect, useCallback, useState, useRef } from 'react';

export interface KeyboardShortcut {
  id: string;
  keys: string;
  defaultKeys: string;
  label: string;
  description: string;
  category: ShortcutCategory;
  action: () => void;
  enabled?: boolean;
}

export type ShortcutCategory =
  | 'navigation'
  | 'search'
  | 'email'
  | 'activities'
  | 'marketing'
  | 'sales'
  | 'communications'
  | 'triveny'
  | 'general';

export type ShortcutDef = Omit<KeyboardShortcut, 'action' | 'defaultKeys'> & { action: () => void };

interface KeyboardShortcutContextType {
  shortcuts: Map<string, KeyboardShortcut>;
  registerShortcut: (shortcut: ShortcutDef) => void;
  unregisterShortcut: (id: string) => void;
  isHelpOpen: boolean;
  setIsHelpOpen: React.Dispatch<React.SetStateAction<boolean>>;
  getShortcutsByCategory: () => Record<string, KeyboardShortcut[]>;
  customizeShortcut: (id: string, newKeys: string) => void;
  resetShortcut: (id: string) => void;
  resetAllShortcuts: () => void;
  getCustomOverrides: () => Record<string, string>;
  getKeysForShortcut: (id: string, defaultKeys: string) => string;
}

const KeyboardShortcutContext = createContext<KeyboardShortcutContextType | null>(null);

const STORAGE_KEY = 'keyboard_shortcut_overrides';

function loadOverrides(): Record<string, string> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch { return {}; }
}

function saveOverrides(overrides: Record<string, string>) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(overrides));
}

/** Parse a shortcut key string like "mod+shift+k" into a descriptor */
export function parseKeys(keys: string) {
  const parts = keys.toLowerCase().split('+');
  return {
    mod: parts.includes('mod'),
    shift: parts.includes('shift'),
    alt: parts.includes('alt'),
    key: parts.filter(p => !['mod', 'shift', 'alt'].includes(p))[0] || '',
  };
}

/**
 * Extract the physical key from a KeyboardEvent.
 * On Mac, Alt/Option modifies `e.key` (e.g. Alt+H → `˙`), so we use
 * `e.code` to get the real key when Alt is held.
 */
function physicalKey(e: KeyboardEvent): string {
  if (e.altKey && e.code) {
    if (e.code.startsWith('Key')) return e.code.slice(3).toLowerCase();
    if (e.code.startsWith('Digit')) return e.code.slice(5);
    return e.code.toLowerCase();
  }
  return e.key.toLowerCase();
}

/** Check if a keyboard event matches a shortcut key string */
export function matchesEvent(e: KeyboardEvent, keys: string): boolean {
  const parsed = parseKeys(keys);
  const modPressed = e.metaKey || e.ctrlKey;

  if (parsed.mod && !modPressed) return false;
  if (!parsed.mod && modPressed) return false;
  if (parsed.shift && !e.shiftKey) return false;
  if (!parsed.shift && e.shiftKey && parsed.mod) return false;
  if (parsed.alt && !e.altKey) return false;
  if (!parsed.alt && e.altKey) return false;

  return physicalKey(e) === parsed.key;
}

/** Format a shortcut key string for display */
export function formatShortcutKeys(keys: string): string {
  const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.userAgent);
  const parts = keys.toLowerCase().split('+');

  return parts.map(p => {
    switch (p) {
      case 'mod': return isMac ? '\u2318' : 'Ctrl';
      case 'shift': return isMac ? '\u21E7' : 'Shift';
      case 'alt': return isMac ? '\u2325' : 'Alt';
      default: return p.toUpperCase();
    }
  }).join(isMac ? '' : '+');
}

/** Convert a KeyboardEvent to a shortcut key string (for recording new bindings) */
export function eventToKeyString(e: KeyboardEvent): string | null {
  const rawKey = e.key.toLowerCase();
  if (['control', 'meta', 'shift', 'alt'].includes(rawKey)) return null;

  const key = physicalKey(e);
  const parts: string[] = [];
  if (e.metaKey || e.ctrlKey) parts.push('mod');
  if (e.shiftKey) parts.push('shift');
  if (e.altKey) parts.push('alt');
  parts.push(key);
  return parts.join('+');
}

/**
 * KeyboardShortcutProvider — manages shortcut display, customization, and help dialog.
 * NOTE: The actual keydown handler lives in GlobalShortcuts.tsx (direct listener).
 * This context is for the help dialog and customization only.
 */
export function KeyboardShortcutProvider({ children }: { children: React.ReactNode }) {
  const shortcutsRef = useRef<Map<string, KeyboardShortcut>>(new Map());
  const [shortcuts, setShortcuts] = useState<Map<string, KeyboardShortcut>>(new Map());
  const overridesRef = useRef<Record<string, string>>(loadOverrides());
  const [isHelpOpen, setIsHelpOpen] = useState(false);

  const registerShortcut = useCallback((shortcut: ShortcutDef) => {
    const overrideKeys = overridesRef.current[shortcut.id];
    const entry: KeyboardShortcut = {
      ...shortcut,
      defaultKeys: shortcut.keys,
      keys: overrideKeys || shortcut.keys,
    };
    shortcutsRef.current.set(shortcut.id, entry);
    setShortcuts(new Map(shortcutsRef.current));
  }, []);

  const unregisterShortcut = useCallback((id: string) => {
    shortcutsRef.current.delete(id);
    setShortcuts(new Map(shortcutsRef.current));
  }, []);

  const customizeShortcut = useCallback((id: string, newKeys: string) => {
    const existing = shortcutsRef.current.get(id);
    if (existing) {
      existing.keys = newKeys;
      shortcutsRef.current.set(id, existing);
      setShortcuts(new Map(shortcutsRef.current));
    }
    overridesRef.current[id] = newKeys;
    saveOverrides(overridesRef.current);
  }, []);

  const resetShortcut = useCallback((id: string) => {
    const existing = shortcutsRef.current.get(id);
    if (existing) {
      existing.keys = existing.defaultKeys;
      shortcutsRef.current.set(id, existing);
      setShortcuts(new Map(shortcutsRef.current));
    }
    delete overridesRef.current[id];
    saveOverrides(overridesRef.current);
  }, []);

  const resetAllShortcuts = useCallback(() => {
    shortcutsRef.current.forEach(s => { s.keys = s.defaultKeys; });
    setShortcuts(new Map(shortcutsRef.current));
    overridesRef.current = {};
    saveOverrides({});
  }, []);

  const getCustomOverrides = useCallback(() => ({ ...overridesRef.current }), []);

  /** Get the current keys for a shortcut, respecting customization overrides */
  const getKeysForShortcut = useCallback((id: string, defaultKeys: string) => {
    return overridesRef.current[id] || defaultKeys;
  }, []);

  const getShortcutsByCategory = useCallback(() => {
    const grouped: Record<string, KeyboardShortcut[]> = {};
    shortcuts.forEach(s => {
      if (s.enabled === false) return;
      if (!grouped[s.category]) grouped[s.category] = [];
      grouped[s.category].push(s);
    });
    return grouped;
  }, [shortcuts]);

  return (
    <KeyboardShortcutContext.Provider value={{
      shortcuts,
      registerShortcut,
      unregisterShortcut,
      isHelpOpen,
      setIsHelpOpen,
      getShortcutsByCategory,
      customizeShortcut,
      resetShortcut,
      resetAllShortcuts,
      getCustomOverrides,
      getKeysForShortcut,
    }}>
      {children}
    </KeyboardShortcutContext.Provider>
  );
}

export function useKeyboardShortcuts() {
  const ctx = useContext(KeyboardShortcutContext);
  if (!ctx) throw new Error('useKeyboardShortcuts must be used within KeyboardShortcutProvider');
  return ctx;
}

/** Register a shortcut on mount and unregister on unmount (for help dialog display) */
export function useRegisterShortcut(shortcut: ShortcutDef) {
  const { registerShortcut, unregisterShortcut } = useKeyboardShortcuts();
  const actionRef = useRef(shortcut.action);
  actionRef.current = shortcut.action;

  useEffect(() => {
    registerShortcut({
      ...shortcut,
      action: () => actionRef.current(),
    });
    return () => unregisterShortcut(shortcut.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shortcut.id, shortcut.enabled, registerShortcut, unregisterShortcut]);
}
